import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import toast from 'react-hot-toast';
import { Shield, Plus, Edit2, X, Trash2 } from 'lucide-react';
import { MENU_PERMISSIONS, MENU_PERMISSION_LABELS } from '../lib/menu';

const LABELS_PERMISSOES = {
  ...MENU_PERMISSION_LABELS,
  usuarios: 'Usuários (acesso)',
  funcoes: 'Funções (perfis)',
  tipos_servico_rota: 'Tipos de serviço (rotas)',
  reposicoes: 'Requisições de reposição',
  relatorios_manutencao: 'Relatórios de Manutenção',
  pesquisas: 'Pesquisas',
};

/** Permissões de módulos suspensos no frontend (API ainda existe). */
const PERMISSOES_OCULTAS = new Set(['notificacoes', 'tarefas']);

const ordenarPermissoes = (keys) => {
  const visiveis = (keys || []).filter((p) => !PERMISSOES_OCULTAS.has(p));
  const noMenu = MENU_PERMISSIONS.filter((p) => visiveis.includes(p));
  const extras = visiveis.filter((p) => !MENU_PERMISSIONS.includes(p));
  return [...noMenu, ...extras];
};

const Funcoes = () => {
  const [funcoes, setFuncoes] = useState([]);
  const [permissoesDisponiveis, setPermissoesDisponiveis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingFuncao, setEditingFuncao] = useState(null);
  const [formData, setFormData] = useState({ nome: '', slug: '', permissoes: [] });

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    try {
      setLoading(true);
      const res = await api.get('/funcoes');
      setFuncoes(res.data?.funcoes || []);
      const daApi = res.data?.permissoes_disponiveis || [];
      setPermissoesDisponiveis(ordenarPermissoes([...MENU_PERMISSIONS, ...daApi]));
    } catch (err) {
      toast.error('Erro ao carregar funções');
    } finally {
      setLoading(false);
    }
  };

  const togglePermissao = (key) => {
    const next = formData.permissoes.includes(key)
      ? formData.permissoes.filter((p) => p !== key)
      : [...formData.permissoes, key];
    setFormData({ ...formData, permissoes: next });
  };

  const sanitizePermissoes = (perms) => {
    const allowed = new Set(
      (permissoesDisponiveis.length ? permissoesDisponiveis : MENU_PERMISSIONS)
        .filter((p) => !PERMISSOES_OCULTAS.has(p))
    );
    return [...new Set((perms || []).filter((p) => allowed.has(p)))];
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const permissoes = sanitizePermissoes(formData.permissoes);
    try {
      if (editingFuncao) {
        await api.patch(`/funcoes/${editingFuncao.id}`, {
          nome: formData.nome,
          slug: formData.slug || undefined,
          permissoes,
        });
        toast.success('Função atualizada!');
      } else {
        await api.post('/funcoes', {
          nome: formData.nome,
          slug: formData.slug || undefined,
          permissoes,
        });
        toast.success('Função criada!');
      }
      setShowModal(false);
      setFormData({ nome: '', slug: '', permissoes: [] });
      setEditingFuncao(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar função');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Excluir esta função? Usuários vinculados impedem a exclusão.')) return;
    try {
      await api.delete(`/funcoes/${id}`);
      toast.success('Função excluída!');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao excluir');
    }
  };

  const openCreate = () => {
    setEditingFuncao(null);
    setFormData({ nome: '', slug: '', permissoes: [] });
    setShowModal(true);
  };

  const openEdit = (f) => {
    setEditingFuncao(f);
    const allowed = new Set(
      (permissoesDisponiveis.length ? permissoesDisponiveis : MENU_PERMISSIONS)
        .filter((p) => !PERMISSOES_OCULTAS.has(p))
    );
    setFormData({
      nome: f.nome,
      slug: f.slug || '',
      // Remove lixo legado (ex.: slug "tecnico" gravado como permissão)
      permissoes: (f.permissoes || []).filter((p) => allowed.has(p)),
    });
    setShowModal(true);
  };

  const listPermissoes = permissoesDisponiveis.length ? permissoesDisponiveis : MENU_PERMISSIONS;

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
            <Shield className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Funções (perfis de acesso)</h1>
            <p className="text-slate-600">Defina o que cada perfil pode acessar no sistema</p>
          </div>
        </div>
        <button
          type="button"
          onClick={openCreate}
          className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white px-6 py-3 rounded-xl font-medium hover:shadow-lg transition-all flex items-center gap-2"
        >
          <Plus className="w-5 h-5" /> Nova função
        </button>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {funcoes.map((f) => (
          <div
            key={f.id}
            className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5 hover:shadow-md transition-shadow"
          >
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="font-bold text-slate-900">{f.nome}</h3>
                {f.slug && <span className="text-xs text-slate-500">{f.slug}</span>}
              </div>
              <div className="flex gap-1">
                <button
                  type="button"
                  onClick={() => openEdit(f)}
                  className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => handleDelete(f.id)}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
            <ul className="text-sm text-slate-600 space-y-1">
              {(() => {
                const perms = (f.permissoes || []).filter((p) => !PERMISSOES_OCULTAS.has(p));
                if (perms.length === 0) return <li className="text-slate-400">Nenhum acesso definido</li>;
                return (
                  <>
                    {perms.slice(0, 5).map((p) => (
                      <li key={p}>{LABELS_PERMISSOES[p] || p}</li>
                    ))}
                    {perms.length > 5 && <li>+ {perms.length - 5} mais</li>}
                  </>
                );
              })()}
            </ul>
          </div>
        ))}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-slate-900">
                {editingFuncao ? 'Editar função' : 'Nova função'}
              </h2>
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="p-2 hover:bg-slate-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Nome</label>
                <input
                  type="text"
                  value={formData.nome}
                  onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none"
                  placeholder="Ex: Técnico, Gerência"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Slug (identificador único, opcional)</label>
                <input
                  type="text"
                  value={formData.slug}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none"
                  placeholder="Ex: tecnico, gerencia"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Acesso (o que esta função pode ver)</label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 border border-slate-200 rounded-xl p-4 bg-slate-50/50">
                  {listPermissoes.map((key) => (
                    <label key={key} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.permissoes.includes(key)}
                        onChange={() => togglePermissao(key)}
                        className="w-4 h-4 text-emerald-600 rounded"
                      />
                      <span className="text-sm">{LABELS_PERMISSOES[key] || key}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 px-4 py-3 rounded-xl border border-slate-200 text-slate-600 font-medium hover:bg-slate-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-3 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-medium hover:shadow-lg"
                >
                  {editingFuncao ? 'Salvar' : 'Criar função'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Funcoes;
